<template>
  <v-dialog v-model="showAddAdditionalCompanyDialog" max-width="1200">
    <v-card v-if="showAddAdditionalCompanyDialog">
      <v-card-title class="primary white--text">
        <span class="headline">
          {{ $t('texts.addAddon') }} test
        </span>
        <v-spacer/>
        <v-btn
          text small fab
          @click="showAddAdditionalCompanyDialog = false"
        >
          <v-icon color="white">close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-stepper v-model="step" vertical class="elevation-0">
          <v-stepper-step step="1" complete>
            {{ $t('texts.chooseAddon') }}
          </v-stepper-step>
          <v-stepper-content step="1" class="py-0">
            <v-card>
              <form data-vv-scope="form-1">
                <v-card-text class="py-0">
                  <v-list two-line>
                    <template v-for="(product, index) in products">
                      <v-list-item :key="product.stripe_id" class="py-3">
                        <v-list-item-content>
                          <v-list-item-title>{{ product.translations.name[language] }}</v-list-item-title>
                          <v-list-item-subtitle class="text--primary">
                            <template v-if="interval === 'monthly'">
                              {{ $t('texts.pricePerMonth') }}:
                            </template>
                            <template v-if="interval === 'yearly'">
                              {{ $t('texts.pricePerYear') }}:
                            </template>
                            <span>CHF {{ product.plans[interval].amount }}</span>
                          </v-list-item-subtitle>
                          <v-list-item-subtitle>
                            {{ product.translations.description[language] }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn
                            color="primary"
                            @click="setAddon(index)"
                            :outlined="selectedStripeId !== product.stripe_id"
                            :disabled="!additionalCompaniesAddable"
                          >
                            {{ $t('texts.choose') }}
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                  </v-list>
                </v-card-text>
              </form>
            </v-card>
          </v-stepper-content>
          <v-stepper-step step="2" complete>
            {{ $t('texts.chooseCompany') }}
          </v-stepper-step>
          <v-stepper-content step="2">
            <v-card>
              <form data-vv-scope="form-2">
                <v-card-text>
                  <v-autocomplete
                    v-model="form.additional_company_id"
                    :label="$t('texts.company.label')"
                    :items="companyItems"
                    item-value="id"
                    item-text="name"
                    v-validate="'required'"
                    data-vv-name="form-2.company_id"
                    :data-vv-as="$t('texts.company.label')"
                    :error-messages="errors.collect('form-2.company_id')"
                    required
                  >
                    <template slot="no-data">
                      <v-list-item :to="{ name: 'add.company' }">
                        <v-list-item-action><v-icon>add</v-icon></v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>{{ $t('texts.addCompany') }}</v-list-item-title>
                          <v-list-item-subtitle>{{ $t('texts.noCompanyWithoutSubscription') }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                  <v-btn
                    outlined text primary
                    :to="{ name: 'add.company' }"
                  >
                    {{ $t('texts.addCompany') }}
                  </v-btn>
                  <v-spacer/>
                  <v-btn
                    color="primary"
                    @click="next()"
                  >
                    {{ $t('texts.next') }}
                  </v-btn>
                </v-card-actions>
              </form>
            </v-card>
          </v-stepper-content>
          <v-stepper-step step="3" complete>
            {{ $t('texts.summary') }}
          </v-stepper-step>
          <v-stepper-content step="3">
            <v-card v-if="selectedProductIndex !== null && form.company_id !== null">
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col :cols="12" :sm="4">
                      {{ $t('texts.subscription') }}
                    </v-col>
                    <v-col :cols="12" :sm="8">
                      {{ products[selectedProductIndex].translations.name[language] }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col :cols="12" :sm="4">
                      {{ $tc('texts.company.label', 1) }}
                    </v-col>
                    <v-col :cols="12" :sm="8">
                      {{ getSelectedCompanyName(form.additional_company_id) }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col :cols="12" :sm="4">
                      <template v-if="interval === 'monthly'">
                        {{ $t('texts.pricePerMonth') }}
                      </template>
                      <template v-if="interval === 'yearly'">
                        {{ $t('texts.pricePerYear') }}
                      </template>
                    </v-col>
                    <v-col :cols="12" :sm="8">
                      CHF {{ products[selectedProductIndex].plans[interval].amount }}
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-divider/>
              <v-card-actions>
                <v-btn
                  color="primary"
                  v-if="step > 1"
                  @click="prev()"
                  text
                >
                  {{ $t('texts.back') }}
                </v-btn>
                <v-spacer/>
                <v-btn
                  color="secondary"
                  v-if="step === 3"
                  :disabled="buttonLoading"
                  :loading="buttonLoading"
                  @click="addAdditionalCompany()"
                >
                  {{ $t('texts.addAddon') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

const { mapState: mapUserState } = createNamespacedHelpers('user');
const { mapState: mapCompanyState } = createNamespacedHelpers('company');
const { mapActions: mapSubscriptionActions } = createNamespacedHelpers('subscription');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'AddAdditionalCompanyDialog',
  props: {
    products: {
      type: Array,
    },
  },
  data() {
    return {
      showAddAdditionalCompanyDialog: false,
      buttonLoading: false,
      additionalCompaniesAddable: false,
      selectedProductIndex: null,
      selectedStripeId: '',
      step: 1,
      interval: 'month',
      form: {
        company_id: null,
        subscription_id: '',
        additional_company_id: null,
      },
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  mounted() {
    this.$eventBus.$on('show-add-additional-company-dialog', (payload) => {
      this.reset();
      this.form.company_id = payload.company_id;
      this.form.subscription_id = payload.subscription_id;
      this.additionalCompaniesAddable = payload.additional_companies_addable;
      this.interval = payload.interval === 'month' ? 'monthly' : 'yearly';
      this.showAddAdditionalCompanyDialog = true;
    });
  },
  computed: {
    ...mapCompanyState([
      'companies',
    ]),
    ...mapUserState([
      'language',
    ]),
    companyItems() {
      return this.companies.filter((company) => !company.active_subscription);
    },
  },
  methods: {
    ...mapSubscriptionActions([
      'storeAdditionalCompany',
    ]),
    ...mapMessageActions([
      'showMessageValidateError',
    ]),
    setAddon(index) {
      this.selectedProductIndex = index;
      this.selectedStripeId = this.products[index].stripe_id;
      this.next();
    },
    getSelectedCompanyName(id) {
      const companyName = this.companies.filter((company) => company.id === id);
      if (companyName.length > 0) {
        return companyName[0].name;
      }
      return '';
    },
    prev() {
      this.step -= 1;
      if (this.step === 1) {
        this.selectedStripeId = '';
      }
    },
    next() {
      const scope = `form-${this.step}`;
      this.$validator.validateAll(scope).then((result) => {
        if (result) {
          this.step += 1;
        } else {
          this.showMessageValidateError();
        }
      });
    },
    reset() {
      this.selectedProductIndex = null;
      this.selectedStripeId = '';
      this.step = 1;
      this.form = {
        company_id: null,
        subscription_id: '',
        additional_company_id: null,
      };
    },
    addAdditionalCompany() {
      this.buttonLoading = true;
      this.storeAdditionalCompany(this.form)
        .then(() => {
          this.buttonLoading = false;
          this.showAddAdditionalCompanyDialog = false;
          this.reset();
        });
    },
  },
};
</script>
